import React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { FriendlyButton } from "../shared";

/**
 * PaymentPreview Component
 * Shows a summary of the donation before final submission
 */
const PaymentPreview = ({
  t,
  donorData,
  amount,
  surchargeEnabled,
  surchargeValue,
  totalAmount,
  paymentType,
  frequency,
  startDate,
  numberOfPayments,
  perPaymentAmount,
  paymentMethod,
  cardLast4,
  cardBrand,
  bankName,
  bankLast4,
  onConfirm,
  onEdit,
  isSubmitting = false,
  submitError = null,
}) => {
  const paymentMethodLabel = paymentMethod === "card"
    ? `${cardBrand || t("card", "Card")} •••• ${cardLast4 || "****"}`
    : `${bankName || t("bank", "Bank")} •••• ${bankLast4 || "****"}`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <ReceiptIcon sx={{ fontSize: 40, color: "primary.main" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          {t("review_donation", "Review Your Donation")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("review_before_confirm", "Please review the details before confirming")}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Donor Info Section */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <PersonIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("donor_information", "Customer Information")}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1}>
            <Typography>
              <strong>{t("name", "Name")}:</strong> {donorData?.first_name} {donorData?.last_name}
            </Typography>
            <Typography>
              <strong>{t("email", "Email")}:</strong> {donorData?.email}
            </Typography>
            {donorData?.phone && (
              <Typography>
                <strong>{t("phone", "Phone")}:</strong> {donorData.phone}
              </Typography>
            )}
          </Stack>
        </Paper>

        {/* Payment Method Section */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <PaymentIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("payment_method", "Payment Method")}
            </Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Typography>{paymentMethodLabel}</Typography>
        </Paper>

        {/* Donation Summary Section */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: "primary.50",
            borderColor: "primary.200",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {t("donation_summary", "Donation Summary")}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">
                {t("donation_type", "Donation Type")}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {paymentType === "one_time"
                  ? t("one_time_donation", "One-Time Donation")
                  : t("split_payment_donation", "Split Payment")}
              </Typography>
            </Stack>

            {paymentType === "split" && (
              <>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    {t("frequency", "Frequency")}
                  </Typography>
                  <Typography sx={{ fontWeight: 500 }}>
                    {t(frequency, frequency)}
                  </Typography>
                </Stack>
                {startDate && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">
                      {t("first_payment_date", "First Payment")}
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {new Date(startDate).toLocaleDateString()}
                    </Typography>
                  </Stack>
                )}
              </>
            )}

            <Divider />

            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">
                {t("donation_amount", "Donation Amount")}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                ${Number(amount).toFixed(2)}
              </Typography>
            </Stack>

            {surchargeEnabled && surchargeValue > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">
                  {t("processing_fee", "Processing Fee")}
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${surchargeValue.toFixed(2)}
                </Typography>
              </Stack>
            )}

            <Divider />

            <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                {t("total", "Total")}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.25rem", color: "primary.main" }}
              >
                ${totalAmount.toFixed(2)}
                {paymentType === "split" && numberOfPayments && perPaymentAmount && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}({numberOfPayments} × ${perPaymentAmount.toFixed(2)})
                  </Typography>
                )}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {/* Error Message */}
        {submitError && (
          <Alert severity="error" sx={{ py: 1.5 }}>
            {submitError}
          </Alert>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2}>
          <FriendlyButton
            variant="outlined"
            onClick={onEdit}
            disabled={isSubmitting}
            sx={{ flex: 1 }}
          >
            {t("edit", "Edit")}
          </FriendlyButton>
          <FriendlyButton
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{ flex: 1 }}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {isSubmitting
              ? t("processing", "Processing...")
              : t("confirm_payment", "Confirm Payment")}
          </FriendlyButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PaymentPreview;
